<template>
  <div v-if="selected">
    <div class="header">
      <div class="title">
        <h2>Plan Summary</h2>
        <p v-if="finalized" style="font-size: 18px">Your Plan Summary is a record of your plan performance and payments. Download a copy for your records.</p>
        <p v-else style="font-size: 18px">Your Plan Summary keeps you informed about your potential award. You will receive email reminders when new forecasts or payments are calculated.</p>
      </div>

      <div
        v-if="summary_message"
        class="message"
      >
        {{ summary_message }}
      </div>
    </div>

    <!-- toolbar -->
    <PlanToolbar
      class="toolbar"
      :pdf="selected.pdf_link"
      :statement="getStatementRoute()"
    />

    <!-- certificates -->
    <div class="certificates-wrapper" v-if="certificates.length > 0">
        <h4>Award certificates</h4>
        <div
          v-for="(cert, i) in certificates"
          :key="'crt-btn_'+i"
          class="plan-details-certificate-wrapper"
        >
          <div class="plan-details-certificate-main">
            <a
              class="link"
              :href="cert.link"
              target="_blank"
            >{{ cert.year }}</a>
          </div>
      </div>
    </div>

    <!-- overview -->
    <PlanCard class="overview-card">
      <div class="s4-overview s4-overview-grid-cols">
        <div id="s4-overview-banner-current" class="s4-overview-banner">Current Value</div>
        <div id="s4-overview-banner-rule"></div>
        <div id="s4-overview-banner-forecasted" class="s4-overview-banner">Forecasted Value</div>
        <section id="s4-overview-headings-current">
          <table class="s4-overview-table-left-right">
            <tbody>
              <tr>
                <td class="s4-td-left">Account</td>
                <td class="s4-td-right" v-if="selected.plan.contributionSpecificInterest">Contribution /<br>Interest Rate</td>
                <td class="s4-td-right" v-else>Contribution</td>
              </tr>
            </tbody>
          </table>
        </section>
        <div id="s4-overview-headings-rule"></div>
        <section id="s4-overview-headings-next-payment">
          <p class="s4-overview-heading">Next<br>Payment</p>
          <p class="s4-overview-value">{{ employee.hasNextPayment ? formatDate(employee.nextPaymentDate) : '' }}</p>
        </section>
        <section id="s4-overview-headings-forecasted-payment">
          <p class="s4-overview-heading">Forecasted<br>Payment $</p>
          <p class="s4-overview-value">{{ employee.hasNextPayment ? formatCurrency(employee.nextPaymentAmount) : '' }}</p>
        </section>
        <section id="s4-overview-headings-cumulative-plan-value">
          <p class="s4-overview-heading">Cumulative Plan<br>Value (EOY)</p>
          <p class="s4-overview-value">{{ formatCurrency(employee.cumulativePlanValue) }}</p>
        </section>
        <section id="s4-overview-values-contributions">
          <p class="s4-main-text" v-if="employee.contributions.length == 0">(no contributions yet)</p>
          <table class="s4-overview-table-left-right" v-else>
            <tbody>
              <tr v-for="(c, n) in employee.contributions.slice().reverse()" :key="n">
                <td class="s4-td-left">{{ c[0] }}</td>
                <td class="s4-td-right" v-if="selected.plan.contributionSpecificInterest">{{ formatCurrency(c[1]) }} / {{ formatPercent4(c[2]) }}</td>
                <td class="s4-td-right" v-else>{{ formatCurrency(c[1]) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
        <section id="s4-overview-values-distributions">
          <table class="s4-overview-table-left-right">
            <tbody>
              <tr>
                <td class="s4-td-left">Current Account Balance</td>
                <td class="s4-td-right-highlight">{{ formatCurrency(employee.currentAccountBalance) }}</td>
              </tr>
              <tr>
                <td class="s4-td-left">Total Distributions To Date</td>
                <td class="s4-td-right-highlight">{{ formatCurrency(employee.distributionsToDate) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
        <div id="s4-overview-values-rule"></div>
        <section id="s4-overview-values-chart">
          <div id="s4-line-chart-wrapper">
            <Line
              :data="chartData"
              :options="chartOptions"
            />
          </div>
          <div id="s4-line-chart-wrapper-mobile">
            <Line
              :key="graph_year"
              :data="mobileChartData"
              :options="chartOptions"
            />
          </div>
          <div class="mobile-chart-controls">
            <span
              class="prev-year"
              :class="{disabled:graph_year == 0}"
              @click="prevGraphYear"
            >&lt;</span>
            <span
              class="next-year"
              :class="{disabled:graph_year + 5 >= employee.forecast.length}"
              @click="nextGraphYear"
            >&gt;</span>
          </div>
          <p class="s4-overview-info">*Cumulative Plan Value projections are based on a hypothetical forecast of future financial performance. This projection is not intended to provide guaranteed predictions of the future payment value of awards.</p>
        </section>
      </div>
    </PlanCard>

    <!-- projection -->
    <PlanCard class="projection-card">
      <div class="s4-projection-title">CUMULATIVE PLAN VALUE FORECAST</div>
      <div class="s4-projection-info">Cumulative Plan Value includes Vested Value, Unvested Value and Cumulative Distributions to date. The forecast displays potential non-guaranteed awards in future years. Actual awards will be determined annually by the company.</div>
      <table id="s4-projection-table">
        <tbody>
          <tr>
            <td>
              <template v-if="hasCompletedYears">
                <input
                  v-model="show_completed"
                  type="checkbox"
                >&nbsp;
                Show Completed Years
              </template>
            </td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              <span
                class="prev-year"
                :class="{disabled:selected_year == projectionYearRange[0]}"
                @click="selectPrevYear"
              >&lt;</span>
              {{ year }}
              <span
                class="next-year"
                :class="{disabled:selected_year == projectionYearRange[projectionYearRange.length - 1]}"
                @click="selectNextYear"
              >&gt;</span>
            </td>
          </tr>
          <tr v-if="selected.plan.show_salary_on_projection">
            <td>{{ ucwords(selected.plan.salaryTerm) }}</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ formatCurrency(employee.forecastedSalary[year]) }}
            </td>
          </tr>
          <tr>
            <td>Distributions (BOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planDistributions[year])
                : formatCurrency(employee.forecastedPlanDistributions[year])
              }}
            </td>
          </tr>
          <tr v-if="selected.plan.will_credit_interest">
            <td>Account Value (BOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planBalance[year] - employee.completed.planContributions[year] - employee.completed.planInterest[year])
                : formatCurrency(employee.forecastedPlanBalance[year] - employee.forecastedPlanContributions[year] - employee.forecastedPlanInterest[year])
              }}
            </td>
          </tr>
          <tr v-if="selected.plan.will_credit_interest">
            <td>Appreciation Credit (EOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planInterest[year])
                : formatCurrency(employee.forecastedPlanInterest[year])
              }}
            </td>
          </tr>
          <tr>
            <td>Annual Cash Award (EOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planContributions[year])
                : formatCurrency(employee.forecastedPlanContributions[year])
              }}
            </td>
          </tr>
          <tr v-if="selected.plan.always_fully_vested == 0 && selected.plan.vesting_enabled">
            <td>Vested Account Value (EOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planVestedValue[year])
                : formatCurrency(employee.forecastedPlanVestedValue[year])
              }}
            </td>
          </tr>
          <tr v-if="selected.plan.always_fully_vested == 0 && selected.plan.vesting_enabled">
            <td>Unvested Account Value (EOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planBalance[year] - employee.completed.planVestedValue[year])
                : formatCurrency(employee.forecastedPlanBalance[year] - employee.forecastedPlanVestedValue[year])
              }}
            </td>
          </tr>
          <tr>
            <td>Account Value (EOY)</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.planBalance[year])
                : formatCurrency(employee.forecastedPlanBalance[year])
              }}
            </td>
          </tr>
          <tr>
            <td>Cumulative Plan Value</td>
            <td
              v-for="year in projectionYearRange"
              :key="year"
            >
              {{ year < (selected.plan.first_shown_year ?? selected.plan.starting_year)
                ? formatCurrency(employee.completed.cumulativeDistributions[year] + employee.completed.planBalance[year])
                : formatCurrency(employee.forecastedCumulativeDistributions[year] + employee.forecastedPlanBalance[year])
              }}
            </td>
          </tr>
          <tr class="s4-projection-disclaimer-tr">
            <td
              class="s4-projection-disclaimer-td"
              :colspan="selected.plan.ending_year + 2 - projectionYearRange[0]"
            >
              *Be advised that all projections presented in this Plan Summary are based on a hypothetical company forecast. Actual company performance will certainly vary from the forecast, and the future value of your awards will be higher or lower than shown.
            </td>
          </tr>
        </tbody>
      </table>
    </PlanCard>
  </div>
</template>

<script>
import { Line } from "vue-chartjs"
import { Chart as ChartJS, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip } from "chart.js"
import PlanToolbar from "@/components/plans/global/PlanToolbar.vue"
import PlanCard from "@/components/plans/global/PlanCard.vue"
import VLcurrency from "@/composables/VLcurrency"
import VLdate from "@/composables/VLdate"

ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip);

export default {
  name: "CashAccountPlanView",

  components: {
    PlanCard,
    PlanToolbar,
    Line
  },

  props: {
    plan: {
      type: Object,
      required: true,
    },
    certificates: {
      type: Array,
      required: true,
    },
    finalized: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selected: null,
      employee: null,
      selected_year: null,
      show_completed: false,
      graph_year: 0,
      chartData: {
        labels: [],
        datasets: [{
          data: [],
        }],
      },
      chartOptions: {
        devicePixelRatio: 2, // prevents blurry chart
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Cumulative Plan Value*',
            font: {
              size: 20,
            },
            color: '#1F3A4D',
          },
          legend: {
            display: false,
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';

                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += '$' + new Intl.NumberFormat().format(Math.round(context.parsed.y));
                }
                return label;
              }
            },
            titleFont: {
              size: 16,
            },
            bodyFont: {
              size: 15,
            },
          },
        },
        layout: {
          padding: 20,
        },
        elements: {
          point: {
            backgroundColor: '#3366cc',
            radius: 7,
            hoverRadius: 7,
          },
          line: {
            borderColor: '#3366cc',
            borderWidth: 3,
            borderDash: [12, 4],
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '$' + new Intl.NumberFormat().format(value);
              },
              padding: 10,
              font: {
                size: 15,
                family: 'Lexend Deca, sans-serif',
              },
              color: '#1F3A4D',
              minor: {
                display: true,
              },
            },
            border: {
              display: false,
            },
            grid: {
              color: '#cdd1d2',
            }
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 15,
                family: 'Lexend Deca, sans-serif',
              },
              color: '#1F3A4D',
              padding: 10,
            },
            offset: true,
          },
        },
      },
    }
  },

  computed: {
    userDataLoaded() {
      return this.$dataDirectory?.userDataLoaded
    },
    userData() {
      return this.$dataDirectory?.userData
    },
    summary_message() {
      return this.plan.current?.summary_message
    },
    clientHighlightColor() {
      let highlight_red = 255;
      let highlight_green = 125;
      let highlight_blue = 1;
      if (this.$dataDirectory.userData?.client_styles) {
        const client_styles = JSON.parse(this.$dataDirectory.userData.client_styles);
        if (client_styles) {
          if ('highlight_red' in client_styles) {
            highlight_red = client_styles.highlight_red;
          }
          if ('highlight_green' in client_styles) {
            highlight_green = client_styles.highlight_green;
          }
          if ('highlight_blue' in client_styles) {
            highlight_blue = client_styles.highlight_blue;
          }
        }
      }
      return 'rgb(' + highlight_red + ',' + highlight_green + ',' + highlight_blue + ')';
    },
    mobileChartData() {
      let oReturn = {
        labels: [],
        datasets: [{
          data: [],
        }],
      }
      for (let i = this.graph_year; i < this.graph_year + 5; i++) {
        oReturn.labels.push(this.employee.forecast[i][0])
        oReturn.datasets[0].data.push(this.employee.forecast[i][1])
      }
      return oReturn;
    },
    projectionYearRange() {
      let aReturn = [];
      let firstShownYear = this.selected.plan.first_shown_year ?? this.selected.plan.starting_year;
      for (let year = this.show_completed ? this.selected.plan.plan_start_year : firstShownYear; year <= this.selected.plan.ending_year; ++year) {
        aReturn.push(year.toString())
      }
      return aReturn;
    },
    hasCompletedYears() {
      let firstShownYear = this.selected.plan.first_shown_year ?? this.selected.plan.starting_year;
      return this.selected.plan.plan_start_year != firstShownYear;
    },
  },

  watch: {
    selected_year: {
      handler(newVal) {
        if (newVal) this.formatTable();
      },
    },
    show_completed: {
      handler(newVal) {
        if (!newVal && this.selected_year < (this.selected.plan.first_shown_year ?? this.selected.plan.starting_year)) {
          this.selected_year = (this.selected.plan.first_shown_year ?? this.selected.plan.starting_year);
        }
      },
    },
  },

  beforeCreate() {},

  mounted() {
    this.selected = this.plan?.current.summary_data

    this.employee = this.selected.employee

    for (let i = 0; i < this.employee.forecast.length; i++) {
      this.chartData.labels.push(this.employee.forecast[i][0])
      this.chartData.datasets[0].data.push(this.employee.forecast[i][1])
    }

    let firstShownYear = this.selected.plan.starting_year
    if ('first_shown_year' in this.selected.plan) {
      firstShownYear = this.selected.plan.first_shown_year
    }
    this.selected_year = firstShownYear
  },

  methods: {
    formatCurrency(value, decimals = 0, currency = 'USD') {
      return VLcurrency.formatCurrency(value, decimals, currency)
    },
    formatDate(d, format = 'YYYY-MM-DD', preset = 'DEFAULT') {
      return VLdate.formatDate(d, format, preset)
    },
    formatPercent4(d) {
      let locale = 'en-US'
      let options = { style: 'percent', minimumFractionDigits: 4 }

      return new Intl.NumberFormat(locale, options).format(d / 10000000)
    },
    getStatementRoute() {
      let plan_id = this.plan?.current.summary_data.plan.id;
      let route = this.$apiManager.route(`plan-statement/${plan_id}`);

      return route;
    },
    ucwords(s) {
      const words = s.split(' ')

      let newWords = []
      for (let n = 0; n < words.length; ++n) {
        const thisWord = words[n]
        newWords.push(thisWord[0].toUpperCase() + thisWord.slice(1))
      }

      return newWords.join(' ')
    },
    formatTable() {
      const table = document.getElementById("s4-projection-table");
      if (!table) {
        setTimeout(() => this.formatTable(), 1000);
        return;
      }
      this.$nextTick(() => {
        const yearIndex = 1 + this.selected_year - this.projectionYearRange[0];
        table.childNodes.forEach((section) => {
          section.childNodes.forEach((row) => {
            let elKey = 0; // keeps track of elements, ignores text nodes
            row.childNodes.forEach((cell, key) => {
              if (key !== 0 && elKey !== yearIndex) {
                cell.classList?.remove('active');
              } else if (cell.nodeType != 3) { // ignore text nodes
                cell.classList?.add('active');
              }
              if (cell.nodeType != 3) { // ignore text nodes
                elKey++;
              }
            });
          });
        });
      })
    },
    selectPrevYear() {
      if (this.selected_year != this.projectionYearRange[0]) {
        this.selected_year--;
      }
    },
    selectNextYear() {
      if (this.selected_year != this.projectionYearRange[this.projectionYearRange.length - 1]) {
        this.selected_year++;
      }
    },
    prevGraphYear() {
      if (this.graph_year != 0) {
        this.graph_year--;
      }
    },
    nextGraphYear() {
      if (this.graph_year + 5 < this.employee.forecast.length) {
        this.graph_year++;
      }
    },
  },
}
</script>

<style scoped lang="scss">
@use "../../scss/_variables.scss";

.header {
  display: flex;
  justify-content: space-between;

  .title {

    h1 {
      font-size: variables.$text-xl; // ~ 32px
      line-height: 1.0625;
      font-weight: 700;
      margin-bottom: 0;
    }
    h2 {
      text-transform: capitalize;
      margin-bottom: 0;
    }
  }

  .message {
    min-width: 400px;
    width: 50%;

    border: 1px solid var(--charcoal-tint-20);
    border-radius: 8px;
    padding: 0.5rem 1rem;
  }
}

h3 {
  font-size: variables.$text-base;
}

.text-highlight {
  color: var(--highlight-color);
}

.font-bold {
  font-weight: bold;
}

.text-md {
  font-size: variables.$text-md;
}

.text-center {
  text-align: center;
}

.toolbar {
  margin-bottom: 1rem;
}

.certificates-wrapper {
  margin-bottom: 2.5rem;
}

.overview-card {
  margin-bottom: 2.5rem;
}

.s4-overview {
  display: grid;

  border: 1px solid #9fadb3;
}

.s4-overview-banner {
  color: #ffffff;
  background-color: #2817e8;
  font-size: 1rem;
  font-weight: bold;
  padding: 10px;
  text-align: center;
}

.s4-overview-grid-cols {
  grid-template-columns: 6fr 1px repeat(3, 5fr);
}

.s4-overview>section {
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: center;
  height:100%;
}

.s4-overview-heading {
  margin: 1em 0 0 0;
  font-size: 1rem;
  font-weight: bold;
  color: #1F3A4D;
  text-align: center;
}

.s4-overview-value {
  margin: 5px 0;
  font-size: 1rem;
  font-weight: bold;
  color: v-bind(clientHighlightColor);
  text-align: center;
}

.s4-overview-info {
  font-size: 14px;
  text-align: center;
  color: #1F3A4D;
  padding: 0 20px;
}

table.s4-overview-table-left-right {
  width: 100%;
}

table.s4-overview-table-left-right td {
  font-size: 1rem;
  font-weight: bold;
  color: #1F3A4D;
  vertical-align: bottom;
}

table.s4-overview-table-left-right td.s4-td-left {
  text-align: left;
}

table.s4-overview-table-left-right td.s4-td-right {
  text-align: right;
}

table.s4-overview-table-left-right td.s4-td-right-highlight {
  color: v-bind(clientHighlightColor);
  text-align: right;
}

#s4-overview-banner-current {
  grid-column: 1;
  grid-row: 1;
}

#s4-overview-banner-rule {
  grid-column: 2;
  grid-row: 1;
  border-right: 1px solid #2817e8;
}

#s4-overview-banner-forecasted {
  grid-column: 3 / 6;
  grid-row: 1;
}

#s4-overview-headings-current {
  grid-column: 1;
  grid-row: 2;
  border-bottom: 1px solid #9fadb3;
}

#s4-overview-headings-rule {
  grid-column: 2;
  grid-row: 2;
  border-right: 1px solid #9fadb3;
}

#s4-overview-headings-next-payment {
  grid-column: 3;
  grid-row: 2;
  border-bottom: 1px solid #9fadb3;
}

#s4-overview-headings-forecasted-payment {
  grid-column: 4;
  grid-row: 2;
  border-bottom: 1px solid #9fadb3;
}

#s4-overview-headings-cumulative-plan-value {
  grid-column: 5;
  grid-row: 2;
  border-bottom: 1px solid #9fadb3;
}

#s4-overview-values-contributions {
  grid-column: 1;
  grid-row: 3;
  justify-content: start;
}

#s4-overview-values-distributions {
  grid-column: 1;
  grid-row: 4;
}

#s4-overview-values-rule {
  grid-column: 2;
  grid-row: 3 / 5;
  border-right: 1px solid #9fadb3;
}

#s4-overview-values-chart {
  grid-column: 3 / 6;
  grid-row: 3 / 5;
}

/* Charts */
#s4-line-chart-wrapper {
  width: 100%;
  min-height: 600px;
}

.projection-card {
  margin: 1em 0;
  border: 0;
}

.s4-projection-title {
  font-size: 1rem;
  font-weight: bold;
  color: #1F3A4D;
}

.s4-projection-info {
  margin: 5px 0;
  font-size: 16px;
  color: #1F3A4D;
}

#s4-projection-table {
  width: 100%;
  border: 1px solid #9fadb3;
}

#s4-projection-table tr {
  border-top: 1px solid #9fadb3;
}

#s4-projection-table td {
  font-size: 1rem;
  color: #1F3A4D;
  text-align: right;
}

#s4-projection-table tr.s4-projection-disclaimer-tr {
  border-top: none;
}

#s4-projection-table td.s4-projection-disclaimer-td {
  font-size: 14px;
  text-align: center;
}

table {
  border-collapse: collapse;
  width:100%;

  tr {
    td {
      padding: 8px;
      min-height: 39px;
    }
  }
}

.prev-year,
.next-year,
#s4-line-chart-wrapper-mobile,
.mobile-chart-controls {
  display: none;
}

@media screen and (max-width: 500px) {
  #s4-projection-table {
    th, td {
      width: unset;
    }
    th:not(.active), td:not(.active){
      display: none;
    }
    th.active, td.active{
      display: table-cell;
    }

    tr:first-child td.active:not(:first-child) {
      font-size: 24px;
      padding: 0px 8px;
    }

    span.next-year {
      padding-left: 8px;
    }
    span.prev-year {
      padding-right: 8px;
    }
  }
  span.next-year, span.prev-year {
    display: unset;
    text-decoration: underline;
    cursor: pointer;
    font-size: 24px;
    color: #241b89;
    &.disabled {
      color: variables.$color-button-disabled;
      cursor: default;
    }
  }

  .mobile-chart-controls {
    width: 100%;
    display: flex;
    justify-content: space-around;
    margin-bottom: 8px;
  }

  .s4-overview {
    display: flex;
    flex-direction: column;
  }

  #s4-overview-banner-current,
  #s4-overview-headings-current,
  #s4-overview-values-contributions,
  #s4-overview-values-distributions {
    order: 1;
  }

  #s4-overview-banner-forecasted,
  #s4-overview-headings-next-payment,
  #s4-overview-headings-forecasted-payment,
  #s4-overview-headings-cumulative-plan-value {
    order: 2;
  }

  #s4-overview-values-chart {
    order: 3;
  }

  #s4-line-chart-wrapper {
    display: none;
  }

  #s4-line-chart-wrapper-mobile {
    display: unset;
    width: 100%;
    height: 300px;
  }
}
</style>